import { CalendarItem } from '@wui/common-library'
import dayjs, { unix } from 'dayjs'
import { IcsAttendee, IcsDuration, IcsEvent } from 'ts-ics'

import { getProfileTimezone } from '@/shared/composables/timezone'
import { Contact } from '@/shared/types/api'
import { getScheduleEventDisplayName } from '@/shared/utils/schedule'

import { withEventTimezone } from './ics-date'

export const SCHEDULE_EVENT_ID_SEPARATOR = '&'

const calendarAttendeeRole = {
  CHAIR: 'CHAIR',
  REQ_PARTICIPANT: 'REQ-PARTICIPANT',
} as const

export type ScheduleOrganizerIdentity = {
  email?: string
  imLogin?: string
  internalNumber?: string
  name?: string
}

export const getCalendarEmailValue = (email?: string): string => {
  if (!email) {
    return ''
  }

  return email.trim().replace(/^mailto:/i, '')
}

export const normalizeCalendarEmail = (email?: string): string => {
  return getCalendarEmailValue(email).toLowerCase()
}

export const hasValidCalendarEmail = (email?: string): boolean => {
  const value = getCalendarEmailValue(email)
  if (!value || /^tel:/i.test(value)) {
    return false
  }

  return value.includes('@')
}

export const isTelCalendarAttendee = (email?: string): boolean => {
  return /^tel:/i.test(getCalendarEmailValue(email))
}

export const isPersistedCalendarAttendee = (email?: string): boolean => {
  return hasValidCalendarEmail(email) || isTelCalendarAttendee(email)
}

/**
 * Нормализует идентификатор участника календаря:
 * mailto:/tel: снимаются, из sip URI берётся номер (не email).
 */
export const extractCalendarParticipantKey = (value?: string): string => {
  const raw = getCalendarEmailValue(value).replace(/^tel:/i, '').trim()
  if (!raw) {
    return ''
  }

  const sipMatch = raw.match(/^<?sip:(?:ROOMS-)?([^@>]+)@[^>]+>?$/i)
  if (sipMatch?.[1]) {
    return sipMatch[1]
  }

  return raw
}

export const toTelCalendarAttendeeEmail = (phone: string): string => {
  const value = extractCalendarParticipantKey(phone) || phone.trim().replace(/^tel:/i, '')
  return `tel:${value}`
}

export const toScheduleOrganizerIdentity = (
  user?: Pick<Contact, 'email' | 'imLogin' | 'internalNumber' | 'name'> | null,
): ScheduleOrganizerIdentity | undefined => {
  if (!user) {
    return undefined
  }

  const identity: ScheduleOrganizerIdentity = {}
  if (user.email !== undefined) identity.email = user.email
  if (user.imLogin !== undefined) identity.imLogin = user.imLogin
  if (user.internalNumber !== undefined) identity.internalNumber = user.internalNumber
  if (user.name !== undefined) identity.name = user.name
  return identity
}

export const resolveScheduleOrganizerMailbox = (identity?: ScheduleOrganizerIdentity): string => {
  const domainFromIdentity = identity?.email?.split('@')[1]?.trim()
  if (domainFromIdentity) {
    return `noreply@${domainFromIdentity}`
  }

  if (typeof location !== 'undefined' && location.hostname) {
    return `noreply@${location.hostname}`
  }

  return 'noreply@localhost'
}

export type ScheduleOrganizerOptions = {
  /** Отображаемое имя ORGANIZER (override; по умолчанию — ФИО создателя) */
  displayName?: string
  /**
   * Добавлять создателя в ATTENDEE, если его ещё нет в списке.
   * По умолчанию true. false — организатор явно убрал себя из участников.
   */
  includeCreatorAsAttendee?: boolean
}

export const isOrganizerAttendee = (
  attendee: Pick<IcsAttendee, 'email' | 'dir'>,
  organizer?: { email?: string },
  identity?: ScheduleOrganizerIdentity,
): boolean => {
  const organizerEmail = normalizeCalendarEmail(organizer?.email ?? identity?.email)
  const attendeeEmail = normalizeCalendarEmail(attendee.email)

  if (organizerEmail && attendeeEmail === organizerEmail) {
    return true
  }

  return Boolean(identity?.imLogin && attendee.dir === identity.imLogin)
}

export const isOrganizerParticipant = (
  value: string,
  identity?: ScheduleOrganizerIdentity,
): boolean => {
  if (!value || !identity) {
    return false
  }

  if (identity.internalNumber && value === identity.internalNumber) {
    return true
  }

  if (identity.imLogin && value === identity.imLogin) {
    return true
  }

  const organizerEmail = normalizeCalendarEmail(identity.email)
  return Boolean(organizerEmail && normalizeCalendarEmail(value) === organizerEmail)
}

export const isOrganizerContact = (
  contact: Pick<Contact, 'email' | 'imLogin' | 'internalNumber' | 'id'>,
  identity?: ScheduleOrganizerIdentity,
): boolean => {
  if (!identity) {
    return false
  }

  if (identity.internalNumber && contact.internalNumber === identity.internalNumber) {
    return true
  }

  if (identity.imLogin && (contact.imLogin === identity.imLogin || contact.id === identity.imLogin)) {
    return true
  }

  const organizerEmail = normalizeCalendarEmail(identity.email)
  return Boolean(organizerEmail && normalizeCalendarEmail(contact.email) === organizerEmail)
}

const omitDefaultAttendeeRole = (attendee: IcsAttendee): IcsAttendee => {
  if (attendee.role !== calendarAttendeeRole.REQ_PARTICIPANT) {
    return attendee
  }

  const { role, ...rest } = attendee
  void role
  return rest
}

export const createCalendarOrganizer = (
  organizer?: { email?: string, name?: string },
  identity?: ScheduleOrganizerIdentity,
  options?: ScheduleOrganizerOptions,
): IcsEvent['organizer'] | undefined => {
  // CN — ФИО создателя.
  // Если создатель сам в ATTENDEE — ORGANIZER = noreply (личный email как ORGANIZER
  // мешает получить invite). Если себя убрал из участников — ставим личный email.
  const displayName = identity?.name || options?.displayName || organizer?.name
  if (!displayName) {
    return undefined
  }

  const identityEmail = getCalendarEmailValue(identity?.email)
  const includeCreatorAsAttendee = options?.includeCreatorAsAttendee !== false
  let email: string

  if (includeCreatorAsAttendee) {
    const existingEmail = getCalendarEmailValue(organizer?.email)
    email = existingEmail
    if (
      !hasValidCalendarEmail(email)
      || (identityEmail && normalizeCalendarEmail(email) === normalizeCalendarEmail(identityEmail))
    ) {
      email = resolveScheduleOrganizerMailbox(identity)
    }
  } else if (hasValidCalendarEmail(identityEmail)) {
    email = identityEmail
  } else {
    email = resolveScheduleOrganizerMailbox(identity)
  }

  if (!hasValidCalendarEmail(email)) {
    return undefined
  }

  return {
    email,
    name: displayName,
  }
}

const addEndFromDuration = (event: IcsEvent): IcsEvent => {
  if (event.end || !event.duration) {
    return event
  }

  const { duration, ...rest } = event
  const endDate = applyDuration(event.start.date, duration)

  return {
    ...rest,
    end: {
      date: endDate,
      type: event.start.type ?? 'DATE-TIME',
    },
  }
}

const applyDuration = (startDate: Date, duration: IcsDuration): Date => {
  let end = dayjs(startDate)

  if (duration.before) {
    end = end.subtract(duration.weeks ?? 0, 'week')
      .subtract(duration.days ?? 0, 'day')
      .subtract(duration.hours ?? 0, 'hour')
      .subtract(duration.minutes ?? 0, 'minute')
      .subtract(duration.seconds ?? 0, 'second')
    return end.toDate()
  }

  end = end.add(duration.weeks ?? 0, 'week')
    .add(duration.days ?? 0, 'day')
    .add(duration.hours ?? 0, 'hour')
    .add(duration.minutes ?? 0, 'minute')
    .add(duration.seconds ?? 0, 'second')

  return end.toDate()
}

const stripInternalAttendeeFields = (event: IcsEvent): IcsEvent => {
  if (!event.attendees?.length) {
    return event
  }

  return {
    ...event,
    attendees: event.attendees.map((attendee) => {
      const { dir, ...rest } = attendee
      return dir ? rest : attendee
    }),
  }
}

export type PrepareIcsExportOptions = {
  includeSequence?: boolean
}

/**
 * LINE SEPARATOR (U+2028).
 * Обход бага @wui/im: при сериализации ICS финальный formatLines делает
 * `.replace(/\\n/g, "\n")` и превращает легальный TEXT-escape `\n` в реальный
 * перевод строки → сервер отвечает «календарь неверный».
 * Храним переносы как U+2028 в wire-ICS; в UI декодируем обратно в `\n`.
 */
export const ICS_MULTILINE_PLACEHOLDER = '\u2028'

export const encodeIcsMultilineText = (value?: string): string | undefined => {
  if (value == null) {
    return value
  }
  return value.replace(/\r\n|\r|\n/g, ICS_MULTILINE_PLACEHOLDER)
}

export const decodeIcsMultilineText = (value?: string): string | undefined => {
  if (value == null) {
    return value
  }
  return value.split(ICS_MULTILINE_PLACEHOLDER).join('\n')
}

const withEncodedMultilineTextFields = (event: IcsEvent): IcsEvent => {
  const description = encodeIcsMultilineText(event.description)
  const summary = encodeIcsMultilineText(event.summary)

  return {
    ...event,
    ...(description !== undefined ? { description } : {}),
    ...(summary !== undefined ? { summary } : {}),
  } as IcsEvent
}

export const omitIcsSequence = (event: IcsEvent): IcsEvent => {
  return Object.fromEntries(
    Object.entries(event).filter(([key]) => key !== 'sequence'),
  ) as IcsEvent
}

export const prepareEventForIcsExport = (
  event: IcsEvent,
  identity?: ScheduleOrganizerIdentity,
  options?: PrepareIcsExportOptions & ScheduleOrganizerOptions,
): IcsEvent => {
  const normalizedEvent = withOrganizerAsAcceptedAttendee(event, identity, options)
  const eventWithEnd = withEncodedMultilineTextFields(addEndFromDuration(normalizedEvent))
  const eventWithTimezone = withEventTimezone(eventWithEnd)

  if (options?.includeSequence) {
    return stripInternalAttendeeFields({
      ...eventWithTimezone,
      sequence: eventWithTimezone.sequence ?? 0,
    })
  }

  return stripInternalAttendeeFields(omitIcsSequence(eventWithTimezone))
}

/**
 * Нормализует событие перед сохранением/экспортом:
 * - ORGANIZER = ФИО создателя; email = noreply если создатель в ATTENDEE, иначе личный email
 * - создатель добавляется как ATTENDEE только если includeCreatorAsAttendee !== false
 * - phone-участники сохраняются как ATTENDEE с email tel:...
 */
export const withOrganizerAsAcceptedAttendee = (
  event: IcsEvent,
  identity?: ScheduleOrganizerIdentity,
  options?: ScheduleOrganizerOptions,
): IcsEvent => {
  const organizer = createCalendarOrganizer(event.organizer, identity, options)

  const attendeesFromEvent = (event.attendees ?? [])
    .filter((attendee) => isPersistedCalendarAttendee(attendee.email))
    // Не тащим в список участников сам ORGANIZER noreply-ящик
    .filter((attendee) => !isOrganizerAttendee(attendee, organizer))
    .map((attendee) => {
      const wasChairAccepted = (
        attendee.role === calendarAttendeeRole.CHAIR
        && attendee.partstat === 'ACCEPTED'
      )

      return {
        ...attendee,
        partstat: wasChairAccepted
          ? 'NEEDS-ACTION' as const
          : (attendee.partstat ?? 'NEEDS-ACTION' as const),
        role: calendarAttendeeRole.REQ_PARTICIPANT,
        rsvp: attendee.rsvp ?? true,
      }
    })

  const attendees = [...attendeesFromEvent]
  const includeCreatorAsAttendee = options?.includeCreatorAsAttendee !== false

  if (includeCreatorAsAttendee && identity && hasValidCalendarEmail(identity.email)) {
    const creatorAlreadyListed = attendees.some((attendee) => (
      isOrganizerAttendee(attendee, undefined, identity)
    ))
    if (!creatorAlreadyListed) {
      attendees.unshift({
        email: getCalendarEmailValue(identity.email),
        ...(identity.name ? { name: identity.name } : {}),
        partstat: 'NEEDS-ACTION',
        role: calendarAttendeeRole.REQ_PARTICIPANT,
        rsvp: true,
        ...(identity.imLogin ? { dir: identity.imLogin } : {}),
      })
    }
  }

  const eventWithoutOrganizer = Object.fromEntries(
    Object.entries(event).filter(([key]) => key !== 'organizer'),
  ) as Omit<IcsEvent, 'organizer'>

  // IcsEvent = duration XOR end; после spread TS не сужает union — явный cast безопасен.
  return {
    ...eventWithoutOrganizer,
    ...(organizer ? { organizer } : {}),
    status: event.status ?? 'CONFIRMED',
    attendees: attendees.map(omitDefaultAttendeeRole),
  } as IcsEvent
}

export const isAttendeeMatchingInternalNumber = (
  attendee: IcsAttendee,
  internalNumber: string,
  findContactByImLogin?: (imLogin: string) => Contact | undefined,
): boolean => {
  if (attendee.email === `tel:${internalNumber}`) {
    return true
  }

  if (attendee.dir) {
    const contact = findContactByImLogin?.(attendee.dir)
    if (contact?.internalNumber === internalNumber) {
      return true
    }
  }

  return false
}
const weekdaysStr = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const

export const getScheduleTimestamps = (event: IcsEvent, from_: number, to_: number): number[] => {
  const timestamps: number[] = []

  const timezone = getProfileTimezone()
  const start = dayjs(event.start.date).tz(timezone)
  const from = unix(from_).tz(timezone).hour(start.hour()).minute(start.minute())
  const to = unix(to_).tz(timezone)

  if (!event.recurrenceRule) {
    return []
  }

  // Если событие уже закончилось
  const until = event.recurrenceRule.until?.date ? dayjs(event.recurrenceRule.until.date).tz(timezone) : null
  if (until && until < from) {
    return []
  }

  // Коллбэк для проверки добавления timestamp'ов
  const addToTimestamps = (date: dayjs.Dayjs) => {
    if (date >= start && (!until || date < to) && (date >= from)) {
      timestamps.push(date.unix())
    }
  }

  if (event.recurrenceRule.byDay) {
    // Повторение по дням недели или будням
    event.recurrenceRule.byDay.forEach(day => {
      const date = from.set('day', weekdaysStr.indexOf(day.day))
      for (let i = 0; i <= 5; i++) {
        addToTimestamps(date.add(i, 'weeks'))
      }
    })
  } else if (event.recurrenceRule.byMonthday) {
    // По дням месяца
    event.recurrenceRule.byMonthday.forEach(monthday => {
      // Если в текущем выбранном месяце меньше дней, чем в выбранных днях события,
      // то пропускаем эти дни
      if (from.daysInMonth() < monthday) {
        return
      }

      const date = from.set('date', monthday)
      addToTimestamps(date)

      // Если в календаре есть 2 одинаковых числа разных месяцев
      if (monthday >= 23) {
        // Предыдущий месяц может не иметь такого дня (напр. 31 в феврале) — проверяем
        const prevMonth = date.subtract(1, 'month')
        if (prevMonth.daysInMonth() >= monthday) {
          addToTimestamps(prevMonth)
        }
      } else if (monthday <= 6) {
        addToTimestamps(date.add(1, 'month'))
      }
    })
  } else if (event.recurrenceRule.interval && event.recurrenceRule.frequency === 'DAILY') {
    // Ежедневно
    const daysInMonth = from.daysInMonth()
    for (let i = 1; i <= daysInMonth; i += event.recurrenceRule.interval) {
      const date = from.set('date', i)
      addToTimestamps(date)
    }
  }

  return timestamps
}


export const generateCalendarItems = (
  from: number,
  to: number,
  pServed: string,
  event: IcsEvent,
  untitledFallback: string,
): CalendarItem[] => {
  const { start, recurrenceRule, summary } = event
  const description = getScheduleEventDisplayName(summary, untitledFallback)
  const items: CalendarItem[] = []

  if (recurrenceRule) {
    const timestamps = getScheduleTimestamps(event, from, to)
    timestamps.forEach((timestamp) => {
      items.push({
        description,
        timestamp,
        id: `${pServed}${SCHEDULE_EVENT_ID_SEPARATOR}${event.uid}`,
      })
    })
  } else {
    const timestamp = dayjs(start.date).unix()
    if (timestamp >= from && timestamp <= to) {
      items.push({
        description,
        timestamp,
        id: `${pServed}${SCHEDULE_EVENT_ID_SEPARATOR}${event.uid}`,
      })
    }
  }

  return items.map((item) => ({
    ...item,
    type: 'accent',
    icon: 'confCall',
  }))
}

export function generateEveryDayItems (roomId: string, description: string, time: string, from: number, to: number): CalendarItem[] {
  const items = [] as CalendarItem[]
  const timezone = getProfileTimezone()
  const [hours, minutes] = time.split(':').map(i => parseInt(i))
  for (let ts = from; ts <= to; ts += 60 * 60 * 24) {
    const date = unix(ts).tz(timezone).hour(hours ?? 0).minute(minutes ?? 0)
    items.push({
      description,
      id: roomId,
      timestamp: date.unix(),
    })
  }
  return items
}

export function generateEveryWorkDayItems (roomId: string, description: string, time: string, from: number, to: number): CalendarItem[] {
  const items: CalendarItem[] = []
  const timezone = getProfileTimezone()

  for (let d = unix(from).tz(timezone); d.isBefore(to * 1000); d = d.add(1, 'day')) {
    if (d.weekday() >= 0 && d.weekday() <= 4) {
      const [hours, minutes] = time.split(':').map(i => parseInt(i))
      const timestamp = d.hour(hours ?? 0).minute(minutes ?? 0).unix()
      items.push({
        description,
        id: roomId,
        timestamp,
      })
    }
  }

  return items
}

export function generateEverySpecialWeekDayItems (roomId: string, description: string, time: string, from: number, to: number, day: number): CalendarItem[] {
  day = day % 7 + 1

  const items = [] as CalendarItem[]
  const timezone = getProfileTimezone()
  const [hours, minutes] = time.split(':').map(i => parseInt(i))
  for (let ts = from; ts <= to; ts += 60 * 60 * 24) {
    const date = unix(ts).tz(timezone)
    if (date.day() === day) {
      items.push({
        description,
        id: roomId,
        timestamp: date.hour(hours ?? 0).minute(minutes ?? 0).unix(),
      })
    }
  }
  return items
}

export function generateEverySpecialMonthDayItems (roomId: string, description: string, time: string, from: number, to: number, dayOfMonth: number): CalendarItem[] {
  const items = [] as CalendarItem[]
  const timezone = getProfileTimezone()
  const [hours, minutes] = time.split(':').map(i => parseInt(i))
  for (let ts = from; ts <= to; ts += 60 * 60 * 24) {
    const date = unix(ts).tz(timezone)
    if (date.date() === dayOfMonth) {
      items.push({
        description,
        id: roomId,
        timestamp: date.hour(hours ?? 0).minute(minutes ?? 0).unix(),
      })
    }
  }
  return items
}
